import { Helmet } from 'react-helmet-async';

/**
 * <Seo /> — composant SEO réutilisable.
 *
 * Centralise title / meta description / canonical / Open Graph / JSON-LD.
 * À utiliser sur chaque page à la place des <Helmet> dispersés.
 *
 * Exemples :
 *   <Seo title="Parfumerie | Ohanoot" description="..." path="/cp/parfumerie/123" />
 *   <Seo title={p.designation} description={...} path={url} image={img} jsonLd={productLd} />
 */
const SITE_NAME = 'Ohanoot';
const SITE_URL = 'https://ohanoot.fr';
const DEFAULT_DESC =
  "Ohanoot : parfumerie orientale, librairie, spiritualité, prêt-à-porter, maison et saveurs du monde. Livraison rapide, paiement sécurisé.";
const DEFAULT_IMAGE = `${SITE_URL}/FavIcon.svg`;

// Non-production hosts (dev/recette/staging) must never be indexed by Google.
// Safe-by-default: only forces noindex when the hostname clearly looks like a
// non-prod environment; an unrecognized host is left alone so this can never
// accidentally noindex production. Mirrors the X-Robots-Tag rule in .htaccess.
const NON_PROD_HOST_RE = /(dev|recette|staging|test|localhost|^\d+\.\d+\.\d+\.\d+$)/i;
const isNonProdHost = () =>
  typeof window !== 'undefined' && NON_PROD_HOST_RE.test(window.location.hostname);

export default function Seo({
  title,
  description,
  path = '',
  image,
  type = 'website',
  noindex = false,
  keywords,
  jsonLd,
}) {
  const fullTitle = title
    ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
    : `${SITE_NAME} — Parfumerie, Librairie & Spiritualité`;
  const desc = (description && String(description).trim()) || DEFAULT_DESC;
  const canonical = `${SITE_URL}${path || ''}`.replace(/\/+$/, '') || SITE_URL;
  const img = image || DEFAULT_IMAGE;
  const jsonLdList = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
  const forceNoindex = noindex || isNonProdHost();

  return (
    <Helmet prioritizeSeoTags>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      {forceNoindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* Alternate-language (hreflang) tags. The site has a single crawlable
          URL per page — the FR/EN toggle swaps text client-side via a
          persisted preference, it doesn't route to a separate /en URL — so
          there is no distinct English URL to point search engines at yet.
          Declaring fr + x-default here is what's actually true of this
          site's URL structure today: French is the language served at this
          URL for any visitor/crawler without a stored preference. Add a real
          hreflang="en" entry once (if) English gets its own routed URLs. */}
      <link rel="alternate" hrefLang="fr" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph (partages réseaux sociaux / WhatsApp) */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Données structurées (JSON-LD) */}
      {jsonLdList.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLdList.length === 1 ? jsonLdList[0] : jsonLdList)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Construit l'objet JSON-LD Product à partir d'un article de l'API.
 * À passer à <Seo jsonLd={buildProductJsonLd(selectedBook, url, language)} />
 */
export function buildProductJsonLd(article, canonicalPath, language = 'fr') {
  if (!article) return null;
  const SITE = SITE_URL;

  // _prix_public_ttc is the same TTC (tax-included) field the product page
  // itself displays, with the same discount formula applied — schema price
  // must equal the displayed price, not the pre-tax catalogue price.
  const basePrice = Number(article?._prix_public_ttc ?? 0);
  const discount = Number(article?.discount ?? 0);
  const price = discount > 0 ? basePrice - basePrice * (discount / 100) : basePrice;

  const inStock = Number(article?._qte_a_terme_calcule ?? 0) > 0;
  const images = (article?.articleimage || [])
    .map((im) => im?.link)
    .filter(Boolean);
  // descriptif is the real product description shown on the page.
  // description_en/description_fr/meta_description are frequently empty,
  // in which case this used to silently fall back to just the product name.
  const description =
    article?.descriptif ||
    article?.meta_description ||
    (language === 'eng' ? article?.description_en : article?.description_fr) ||
    article?.designation;

  const ld = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: article?.designation,
    description: stripHtml(description),
    sku: String(article?.id ?? ''),
    image: images.length ? images : [DEFAULT_IMAGE],
    url: `${SITE}${canonicalPath || ''}`,
  };
  if (article?.isbn) ld.gtin13 = String(article.isbn);
  // dc_isbn_editeur is the real brand/publisher/maker field used everywhere
  // else in the app — article_famille.parent.nom is the CATEGORY, not the
  // brand, and was being used as a brand fallback here.
  const brandName = article?.marque?.nom || article?.dc_isbn_editeur;
  if (brandName) {
    ld.brand = {
      '@type': 'Brand',
      name: brandName,
    };
  }

  // AggregateRating / Review — mirrors the exact calculation the reviews
  // tab itself displays (Book Details Page/View Tab and review/Rating
  // Summary/RatingSummary.jsx: sum of all bookreview[].rate / count). Only
  // added when there's at least one real review — Google explicitly
  // disallows rating markup on products with zero reviews.
  const reviews = Array.isArray(article?.bookreview) ? article.bookreview : [];
  if (reviews.length > 0) {
    const totalRating = reviews.reduce((sum, r) => sum + (parseFloat(r?.rate) || 0), 0);
    const ratingValue = totalRating / reviews.length;
    ld.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingValue.toFixed(1),
      reviewCount: reviews.length,
      bestRating: '5',
      worstRating: '1',
    };

    // Only the star-rated reviews ("Rate & Review", is_counted) become
    // individual Review entries — plain comments have no rating to report.
    const namedReviews = reviews
      .filter((r) => r?.is_counted && Number(r?.rate) > 0)
      .map((r) => {
        const authorName = [r?.user?.first_name, r?.user?.last_name].filter(Boolean).join(' ').trim();
        return {
          '@type': 'Review',
          author: { '@type': 'Person', name: authorName || SITE_NAME },
          ...(r?.date ? { datePublished: r.date } : {}),
          ...(r?.description ? { reviewBody: stripHtml(r.description) } : {}),
          reviewRating: {
            '@type': 'Rating',
            ratingValue: Number(r.rate).toFixed(1),
            bestRating: '5',
            worstRating: '1',
          },
        };
      });
    if (namedReviews.length > 0) ld.review = namedReviews;
  }

  if (price > 0) {
    ld.offers = {
      '@type': 'Offer',
      url: `${SITE}${canonicalPath || ''}`,
      priceCurrency: 'EUR',
      price: price.toFixed(2),
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    };
  }
  return ld;
}

function stripHtml(s) {
  return s ? String(s).replace(/<[^>]*>/g, '').trim().slice(0, 500) : undefined;
}

/**
 * Génère une meta description à partir des données de la page quand aucune
 * description CMS (meta_description / description1 / description2...) n'est
 * renseignée — au lieu de retomber sur DEFAULT_DESC (la même description
 * générique pour tout le site). `name` est le nom du produit ou de la
 * catégorie, `brand` la marque/l'éditeur si connu (sinon Ohanoot).
 */
export function buildCatalogDescription({ name, brand } = {}) {
  const subject = (name || '').toString().trim();
  if (!subject) return null;
  const brandName = (brand || '').toString().trim() || SITE_NAME;
  return `Achetez ${subject} - ${brandName}, livraison rapide en France. Paiement sécurisé et retours faciles chez ${SITE_NAME}.`;
}

/**
 * Construit l'objet JSON-LD Person à partir d'un collaborateur de l'API.
 * À passer à <Seo jsonLd={buildPersonJsonLd(CollaboratorData, url)} />
 */
export function buildPersonJsonLd(collaborator, canonicalPath) {
  if (!collaborator) return null;
  const SITE = SITE_URL;

  const ld = {
    '@context': 'https://schema.org/',
    '@type': 'Person',
    name: collaborator?.nom,
    description: stripHtml(collaborator?.meta_description || collaborator?.biographie),
    image: collaborator?.image || DEFAULT_IMAGE,
    url: `${SITE}${canonicalPath || ''}`,
  };
  if (collaborator?.type?.name) {
    ld.jobTitle = collaborator.type.name;
  }
  return ld;
}

/**
 * Construit l'objet JSON-LD Brand à partir d'un éditeur/marque de l'API.
 * À passer à <Seo jsonLd={buildBrandJsonLd(CollaboratorData, url)} />
 */
export function buildBrandJsonLd(publisher, canonicalPath) {
  if (!publisher) return null;
  const SITE = SITE_URL;

  return {
    '@context': 'https://schema.org/',
    '@type': 'Brand',
    name: publisher?.title || publisher?.nom,
    description: stripHtml(publisher?.meta_description || publisher?.description || publisher?.biographie),
    logo: publisher?.image || DEFAULT_IMAGE,
    url: `${SITE}${canonicalPath || ''}`,
  };
}

/**
 * Construit un objet JSON-LD BreadcrumbList à partir d'un tableau de
 * breadcrumbs `{ en, fr, url }` (le même format que celui déjà utilisé un
 * peu partout dans l'app pour alimenter <Breadcrumb paths={...} />).
 */
export function buildBreadcrumbJsonLd(paths, language = 'fr') {
  if (!Array.isArray(paths) || paths.length === 0) return null;

  const items = paths
    .map((p) => {
      if (!p) return null;
      const name = typeof p === 'string' ? p : (language === 'eng' ? p.en : p.fr);
      return typeof name === 'string' && name.trim() !== '' ? { name: name.trim(), url: p?.url } : null;
    })
    .filter(Boolean);

  if (items.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url ? { item: `${SITE_URL}${item.url}` } : {}),
    })),
  };
}

/**
 * Construit un objet JSON-LD FAQPage à partir des données retournées par
 * /faq-questions (utilisées par <FAQSection />) : chaque entrée a
 * `question_fr`/`question_en` et `answers[0].answer_fr`/`answer_en`.
 */
export function buildFaqJsonLd(faqData, language = 'fr') {
  if (!Array.isArray(faqData) || faqData.length === 0) return null;

  const mainEntity = faqData
    .map((card) => {
      const question = language === 'eng' ? card?.question_en : card?.question_fr;
      const answer = language === 'eng' ? card?.answers?.[0]?.answer_en : card?.answers?.[0]?.answer_fr;
      if (!question || !answer) return null;
      return {
        '@type': 'Question',
        name: stripHtml(question),
        acceptedAnswer: { '@type': 'Answer', text: stripHtml(answer) },
      };
    })
    .filter(Boolean);

  if (mainEntity.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

/**
 * <JsonLd /> — injecte un ou plusieurs blocs JSON-LD sans toucher au
 * title/description/canonical de la page. À utiliser sur les pages qui ont
 * déjà leur propre gestion de title/meta et n'ont besoin que de données
 * structurées additionnelles (ex: BreadcrumbList).
 *
 * Exemple :
 *   <JsonLd items={[buildBreadcrumbJsonLd(breadcrumbPaths, language)]} />
 */
export function JsonLd({ items }) {
  const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(list.length === 1 ? list[0] : list)}
      </script>
    </Helmet>
  );
}

/**
 * Construit un objet JSON-LD ItemList à partir d'une liste de produits/
 * collections affichés sur une page listing (catégorie, recherche, meilleures
 * ventes...). `items` = [{ name, url, image }].
 */
export function buildItemListJsonLd(items, canonicalPath) {
  if (!Array.isArray(items) || items.length === 0) return null;

  const itemListElement = items
    .map((it, index) => {
      if (!it?.name || !it?.url) return null;
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: it.name,
        url: /^https?:\/\//.test(it.url) ? it.url : `${SITE_URL}${it.url}`,
        ...(it.image ? { image: it.image } : {}),
      };
    })
    .filter(Boolean);

  if (itemListElement.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: `${SITE_URL}${canonicalPath || ''}`,
    itemListElement,
  };
}

/**
 * Construit un objet JSON-LD VideoObject pour une vidéo présentée sur une
 * fiche produit (YouTube en priorité — Instagram/TikTok n'ont pas de rich
 * results Google mais peuvent aussi être déclarés si un embedUrl existe).
 * `video` = { name, description, thumbnailUrl, uploadDate, embedUrl, contentUrl }.
 */
export function buildVideoObjectJsonLd(video) {
  if (!video?.name || (!video?.embedUrl && !video?.contentUrl)) return null;

  const ld = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.name,
    description: video.description ? stripHtml(video.description) : video.name,
    thumbnailUrl: [video.thumbnailUrl || DEFAULT_IMAGE],
    uploadDate: video.uploadDate || new Date().toISOString().slice(0, 10),
  };
  if (video.embedUrl) ld.embedUrl = video.embedUrl;
  if (video.contentUrl) ld.contentUrl = video.contentUrl;
  return ld;
}

/**
 * Construit un objet JSON-LD BlogPosting à partir d'un article de blog.
 * `post` a les champs `title`, `introduction`/`description`, `image`,
 * `created_at`, `user.first_name`/`user.last_name`.
 */
export function buildBlogPostingJsonLd(post, canonicalPath) {
  if (!post?.title) return null;
  const url = `${SITE_URL}${canonicalPath || ''}`;
  const authorName = [post?.user?.first_name, post?.user?.last_name].filter(Boolean).join(' ').trim() || SITE_NAME;

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: stripHtml(post?.introduction || post?.description) || post.title,
    image: [post?.image || DEFAULT_IMAGE],
    datePublished: post?.created_at,
    dateModified: post?.updated_at || post?.created_at,
    author: { '@type': 'Person', name: authorName },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: DEFAULT_IMAGE },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };
}

/**
 * Construit l'objet JSON-LD Organization pour la home. `sameAs` est
 * construit dynamiquement depuis `companySettings` (authCtx) — jamais codé
 * en dur, les réseaux sociaux venant du back-office et pouvant changer.
 */
export function buildOrganizationJsonLd(companySettings) {
  const sameAs = ['facebook', 'twitter', 'insta', 'instagram', 'youtube', 'linkedin', 'tiktok', 'snapchat']
    .map((key) => companySettings?.[key])
    .filter((v) => typeof v === 'string' && v.trim() !== '');

  const ld = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
  };
  if (sameAs.length > 0) ld.sameAs = sameAs;
  if (companySettings?.email || companySettings?.phone) {
    ld.contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      ...(companySettings?.email ? { email: companySettings.email } : {}),
      ...(companySettings?.phone ? { telephone: companySettings.phone } : {}),
    };
  }
  return ld;
}

/** Construit l'objet JSON-LD WebSite pour la home. */
export function buildWebSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  };
}

/**
 * Construit l'objet JSON-LD LocalBusiness pour la page contact, à partir de
 * `companySettings` (adresse/email/téléphone viennent du back-office).
 * Retourne null si aucune adresse n'est disponible (mieux vaut ne rien
 * émettre qu'un LocalBusiness sans adresse).
 */
export function buildLocalBusinessJsonLd(companySettings) {
  const address = companySettings?.location || companySettings?.address;
  if (!address) return null;

  const sameAs = ['facebook', 'twitter', 'insta', 'instagram', 'youtube', 'linkedin', 'tiktok', 'snapchat']
    .map((key) => companySettings?.[key])
    .filter((v) => typeof v === 'string' && v.trim() !== '');

  const ld = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    url: SITE_URL,
    image: DEFAULT_IMAGE,
    address: { '@type': 'PostalAddress', streetAddress: address },
  };
  if (companySettings?.email) ld.email = companySettings.email;
  if (companySettings?.phone) ld.telephone = companySettings.phone;
  if (sameAs.length > 0) ld.sameAs = sameAs;
  return ld;
}
